class SafetyService {
  constructor($document, $window, $log, $timeout, $rootScope, UrlConfigService, Authenticator) {
    this.$log = $log;
    this.$document = $document[0];
    this.$window = $window;
    this.$timeout = $timeout;
    this.$rootScope = $rootScope;
    this.urlConfigService = UrlConfigService;
    this.authenticator = Authenticator;
    this.isLocalhost = this.urlConfigService.location.host() === 'ce-local.ttl.dev.br';
    this.remoteEntryUrl = this.isLocalhost
      ? `https://localhost:3002/remoteEntry.js?v=${Date.now()}`
      : `${this.urlConfigService.getSafetyComponentsUrl()}/remoteEntry.js?v=${Date.now()}`;
    this.reactLoaded = false;
    this.reactDOMLoaded = false;
    this.remoteEntryLoaded = false;
    this.initializationPromise = null;
    this.userpilotService = null;
    this.initialize();
    this.roots = {};
  }

  initialize() {
    if (!this.initializationPromise) {
      this.initializationPromise = Promise.all([
        this.loadReactAndReactDOM(),
        this.loadRemoteEntry()
      ]).then(() => {
        this.reactLoaded = true;
        this.remoteEntryLoaded = true;
        this.initializeLeafletService('safetyApp')
          .then(leafletService => leafletService())
          .then(leafletModule => {
            const {initLeaflet} = leafletModule;
            initLeaflet();
          });
      }).catch(error => {
        this.$log.error('Error loading React or RemoteEntry:', error);
      });
    }
    return this.initializationPromise;
  }

  loadReactAndReactDOM() {
    if (this.reactLoaded && this.reactDOMLoaded) {
      return Promise.resolve();
    }

    // Create a module script that imports React and exposes it to window
    const moduleScript = this.$document.createElement('script');
    moduleScript.type = 'module';
    moduleScript.textContent = `
      import { React, ReactDOM, ReactDOMClient } from '${this.urlConfigService.getSafetyReactBundleUrl()}/react.bundle.js';

      window.React = React;
      window.ReactDOM = { ...ReactDOM, createRoot: ReactDOMClient.createRoot };

      window.dispatchEvent(new CustomEvent('reactLoaded'));
    `;

    return new Promise((resolve, reject) => {
      const handleReactLoaded = () => {
        this.reactLoaded = true;
        this.reactDOMLoaded = true;
        this.$window.removeEventListener('reactLoaded', handleReactLoaded);
        resolve();
      };

      this.$window.addEventListener('reactLoaded', handleReactLoaded);

      // Handle script errors
      moduleScript.onerror = () => {
        this.$window.removeEventListener('reactLoaded', handleReactLoaded);
        reject(new Error('An error occurred while loading React modules'));
      };

      this.$document.head.appendChild(moduleScript);
    });
  }

  loadRemoteEntry() {
    return new Promise((resolve, reject) => {
      const script = this.$document.createElement('script');
      script.src = this.remoteEntryUrl;
      script.onload = resolve;
      if (this.isLocalhost) {
        script.crossOrigin = 'anonymous';
      }
      script.onerror = () => reject(new Error('An error occurred while loading remoteEntry.js'));
      this.$document.head.appendChild(script);
    });
  }

  loadReactComponent(appName, componentPath, componentId, props) {
    this.unmountReactComponent(componentId);
    return this.getComponentWithRetry(appName, componentPath)
      .then((component) => {
        const container = this.$document.getElementById(componentId);
        if (container) {
          let root = this.roots[componentId];
          if (!root) {
            root = this.$window.ReactDOM.createRoot(container);
            this.roots[componentId] = root;
          }
          root.render(
            this.$window.React.createElement(component().default, props)
          );
        }
      })
      .catch((error) => {
        this.$log.error(`Error loading component at ${componentPath}: `, error);
      });
  }

  unmountReactComponent(componentId) {
    const container = this.$document.getElementById(componentId),
        root = this.roots[componentId];
    if (container && root) {
      root.unmount();
      delete this.roots[componentId];
    }
  }

  getComponentWithRetry(appName, componentName, retries = 5, delay = 1000) {
    return new Promise((resolve, reject) => {
      const attempt = () => {
        this.getComponent(appName, componentName)
          .then(resolve)
          .catch((error) => {
            if (retries === 0) {
              reject(error);
            } else {
              this.$timeout(() => {
                retries--;
                attempt();
              }, delay);
            }
          });
      };
      attempt();
    });
  }

  getComponent(appName, componentName) {
    return new Promise((resolve, reject) => {
      if (!this.$window || !this.$window[appName]) {
        reject(new Error(`${appName} is not loaded or does not exist`));
      } else {
        const checkAppLoaded = () => {
          if (!this.$window[appName] || Object.keys(this.$window[appName]).length === 0) {
            reject(new Error(`${appName} is empty or not exporting anything`));
          } else {
            resolve(this.$window[appName].get(componentName));
          }
        };

        if (!this.$window[appName]) {
          this.$window.addEventListener('appLoaded', checkAppLoaded);
        } else {
          checkAppLoaded();
        }
      }
    });
  }

  initializeLeafletService(appName) {
    return new Promise((resolve, reject) => {
      if (!this.$window || !this.$window[appName]) {
        reject(new Error(`${appName} is not loaded or does not exist`));
      } else {
        const checkAppLoaded = () => {
          if (!this.$window[appName] || Object.keys(this.$window[appName]).length === 0) {
            reject(new Error(`${appName} is empty or not exporting anything`));
          } else {
            resolve(this.$window[appName].get('./LeafletService'));
          }
        };

        if (!this.$window[appName]) {
          this.$window.addEventListener('appLoaded', checkAppLoaded);
        } else {
          checkAppLoaded();
        }
      }
    });
  }

  initializeUserpilot() {
    this.getComponentWithRetry('safetyApp', './UserpilotService')
      .then(userpilotModule => userpilotModule())
      .then(service => {
        this.userpilotService = service;
        this.authenticator.getUser().then(user => {
          service.identifyUser(user);
        });
        this.$rootScope.$on('$stateChangeSuccess', () => {
          service.reloadUserpilot();
        });
      })
      .catch(error => {
        this.$log.error('Error loading UserpilotService:', error);
      });
  }
}
SafetyService.$inject = ['$document', '$window', '$log', '$timeout', '$rootScope', 'UrlConfigService', 'Authenticator'];

angular.module('safetyModule', []).service('safetyService', SafetyService);
